import type { AgentEvaluation, AgentVersion } from '../api/agents.api';

/**
 * What changed between two released versions of an Agent Skill, worked out in the browser
 * from the two records the page already holds.
 *
 * **A line diff written here rather than a library**, because the one thing needed
 * is a longest-common-subsequence over the lines of two prompts a few hundred lines
 * long, which is thirty lines of code and a table of a few hundred squared cells.
 * A dependency is a thing to approve and to keep patched; this is not worth either.
 */

export type DiffLine = { kind: 'same' | 'added' | 'removed'; text: string };

/** What is drawn: a line, or a note that a run of unchanged lines was left out. */
export type DiffRow = DiffLine | { kind: 'skipped'; count: number };

/**
 * The lines of `from` and `to` in order, each marked as in both, only in `to`
 * (added) or only in `from` (removed).
 *
 * **Where a block was replaced, the removed lines come first**, which is how a
 * reader expects "this became that" to read.
 */
export const diffLines = (from: string, to: string): DiffLine[] => {
  const a = from.split('\n');
  const b = to.split('\n');

  // lcs[i][j] is the length of the longest run the two share, looking only at
  // a[i..] and b[j..], filled from the ends so the walk below can go forwards.
  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i]![j] =
        a[i] === b[j]
          ? (lcs[i + 1]?.[j + 1] ?? 0) + 1
          : Math.max(lcs[i + 1]?.[j] ?? 0, lcs[i]?.[j + 1] ?? 0);
    }
  }

  const lines: DiffLine[] = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      lines.push({ kind: 'same', text: a[i] ?? '' });
      i += 1;
      j += 1;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      lines.push({ kind: 'removed', text: a[i] ?? '' });
      i += 1;
    } else {
      lines.push({ kind: 'added', text: b[j] ?? '' });
      j += 1;
    }
  }

  for (; i < a.length; i += 1) {
    lines.push({ kind: 'removed', text: a[i] ?? '' });
  }
  for (; j < b.length; j += 1) {
    lines.push({ kind: 'added', text: b[j] ?? '' });
  }

  return lines;
};

/**
 * The changed lines with `context` unchanged ones either side, and every longer
 * unchanged run replaced by one note saying how many lines it was. **A prompt is a
 * few hundred lines and a change is a few of them**, so showing all of it buries the
 * change; showing none of the lines around it loses what it was changing.
 *
 * A run only one line past the context is kept rather than replaced by a note that
 * takes the same space to say "1 unchanged line".
 */
export const collapseUnchanged = (lines: DiffLine[], context = 2): DiffRow[] => {
  const keep = lines.map((line) => line.kind !== 'same');

  // Spread each change out to the lines around it.
  const near = [...keep];
  keep.forEach((changed, index) => {
    if (!changed) {
      return;
    }
    for (let offset = -context; offset <= context; offset += 1) {
      if (index + offset >= 0 && index + offset < near.length) {
        near[index + offset] = true;
      }
    }
  });

  const rows: DiffRow[] = [];
  let skipped = 0;
  // The last unchanged line seen in the current run, kept so a run of exactly one
  // can be drawn as that line.
  let pendingText = '';

  const flush = () => {
    if (skipped === 0) {
      return;
    }
    // One skipped line is just that line: a note would be the same height.
    if (skipped === 1) {
      rows.push({ kind: 'same', text: pendingText });
    } else {
      rows.push({ kind: 'skipped', count: skipped });
    }
    skipped = 0;
  };

  lines.forEach((line, index) => {
    if (near[index]) {
      flush();
      rows.push(line);
    } else {
      skipped += 1;
      pendingText = line.text;
    }
  });
  flush();

  return rows;
};

/** A case's content, which is everything about it but its id. */
const contentOf = (evaluation: AgentEvaluation): string =>
  JSON.stringify([
    evaluation.input,
    evaluation.fixtures,
    evaluation.expectedOutput,
    evaluation.expectations,
  ]);

export type VersionComparison = {
  /** True when nothing a reader can see differs between the two. */
  identical: boolean;
  title?: { from: string; to: string };
  summary?: { from: string; to: string };
  methods: { added: string[]; removed: string[] };
  promptChanged: boolean;
  /** Empty when the prompt is unchanged. */
  prompt: DiffRow[];
  evaluations: { added: string[]; removed: string[]; changed: string[] };
};

/**
 * What it took to get from `from` to `to`: so `from` is the version being compared
 * against and `to` is the one on screen, and an "added" line is one that the version
 * on screen has and the other does not.
 *
 * **Methods are matched by id and cases by name.** A method's name can be renamed in
 * Confluence without it being a different method, and a case's id is its position in
 * a file, so a case moved up the list is not a new case.
 */
export const compareAgentVersions = (from: AgentVersion, to: AgentVersion): VersionComparison => {
  const fromMethods = new Map(from.methods.map((method) => [method.id, method.name]));
  const toMethods = new Map(to.methods.map((method) => [method.id, method.name]));

  const methods = {
    added: [...toMethods].filter(([id]) => !fromMethods.has(id)).map(([, name]) => name),
    removed: [...fromMethods].filter(([id]) => !toMethods.has(id)).map(([, name]) => name),
  };

  const fromCases = new Map(from.evaluations.map((entry) => [entry.name, entry]));
  const toCases = new Map(to.evaluations.map((entry) => [entry.name, entry]));

  const evaluations = {
    added: [...toCases.keys()].filter((name) => !fromCases.has(name)),
    removed: [...fromCases.keys()].filter((name) => !toCases.has(name)),
    changed: [...toCases]
      .filter(([name, entry]) => {
        const before = fromCases.get(name);
        return before !== undefined && contentOf(before) !== contentOf(entry);
      })
      .map(([name]) => name),
  };

  const promptChanged = from.prompt !== to.prompt;
  const titleChanged = from.title !== to.title;
  const summaryChanged = from.summary !== to.summary;

  return {
    identical:
      !promptChanged &&
      !titleChanged &&
      !summaryChanged &&
      methods.added.length === 0 &&
      methods.removed.length === 0 &&
      evaluations.added.length === 0 &&
      evaluations.removed.length === 0 &&
      evaluations.changed.length === 0,
    ...(titleChanged ? { title: { from: from.title, to: to.title } } : {}),
    ...(summaryChanged ? { summary: { from: from.summary, to: to.summary } } : {}),
    methods,
    promptChanged,
    prompt: promptChanged ? collapseUnchanged(diffLines(from.prompt, to.prompt)) : [],
    evaluations,
  };
};
